/**
 * Search Utility Functions
 * Comprehensive search parsing and normalization for vehicles, customers, and more
 */

// =====================================================
// QUERY NORMALIZATION
// =====================================================

/**
 * Normalize a search query by removing special characters,
 * extra whitespace, and converting to lowercase
 */
export function normalizeQuery(query: string): string {
  if (!query) return '';

  return query
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s@.-]/g, ''); // Keep alphanumeric, spaces, @, ., and -
}

/**
 * Normalize phone number by removing all non-numeric characters
 * Examples:
 *   "0412 345 678" -> "0412345678"
 *   "+61 412 345 678" -> "61412345678"
 *   "(04) 1234-5678" -> "0412345678"
 */
export function normalizePhoneNumber(phone: string): string {
  if (!phone) return '';
  return phone.replace(/[^0-9]/g, '');
}

/**
 * Normalize registration/rego by removing spaces and converting to uppercase
 * Examples:
 *   "ABC 123" -> "ABC123"
 *   "abc123" -> "ABC123"
 */
export function normalizeRegistration(rego: string): string {
  if (!rego) return '';
  return rego.replace(/\s/g, '').toUpperCase();
}

/**
 * Normalize VIN by removing spaces and converting to uppercase
 */
export function normalizeVIN(vin: string): string {
  if (!vin) return '';
  return vin.replace(/\s/g, '').toUpperCase();
}

// =====================================================
// VEHICLE QUERY PARSING
// =====================================================

export interface ParsedVehicleQuery {
  originalQuery: string;
  year?: number;
  make?: string;
  model?: string;
  color?: string;
  keywords: string[];
  isVIN?: boolean;
  isRegistration?: boolean;
  isStockNumber?: boolean;
}

/**
 * Parse a vehicle search query to extract structured components
 * Examples:
 *   "2020 toyota camry" -> { year: 2020, make: "toyota", model: "camry" }
 *   "red hilux" -> { color: "red", make: "hilux" }
 *   "ABC123" -> { isRegistration: true, keywords: ["ABC123"] }
 *   "JT2BF18K9X0123456" -> { isVIN: true }
 */
export function parseVehicleQuery(query: string): ParsedVehicleQuery {
  const normalized = normalizeQuery(query);
  const words = normalized.split(' ').filter(w => w.length > 0);

  const result: ParsedVehicleQuery = {
    originalQuery: query,
    keywords: [],
  };

  // Check if it's a VIN (17 characters, alphanumeric)
  if (/^[A-HJ-NPR-Z0-9]{17}$/i.test(query.replace(/\s/g, ''))) {
    result.isVIN = true;
    result.keywords.push(normalizeVIN(query));
    return result;
  }

  // Check if it looks like a stock number FIRST (e.g., STK123, #123, or just digits)
  // This must come before registration check because pure digits could match both
  if (/^(#|stk|stock)?[0-9]{1,6}$/i.test(query.replace(/\s/g, ''))) {
    result.isStockNumber = true;
    result.keywords.push(query.replace(/[^0-9]/g, ''));
    return result; // Return early to prevent registration check
  }

  // Check if it looks like a registration (short alphanumeric, typically 3-7 chars)
  // Only check this if it's NOT purely numeric (stock numbers handled above)
  if (/^[A-Z0-9]{3,7}$/i.test(query.replace(/\s/g, '')) && !/^\d+$/.test(query)) {
    result.isRegistration = true;
    result.keywords.push(normalizeRegistration(query));
  }

  // Common colors to extract
  const colors = ['red', 'blue', 'white', 'black', 'silver', 'grey', 'gray', 'green',
                  'yellow', 'orange', 'brown', 'gold', 'beige', 'burgundy'];

  for (const word of words) {
    // Extract year (4-digit number between 1900-2100)
    const yearMatch = word.match(/^(19|20)\d{2}$/);
    if (yearMatch && !result.year) {
      result.year = parseInt(word, 10);
      continue;
    }

    // Extract color
    if (colors.includes(word) && !result.color) {
      result.color = word;
      continue;
    }

    // Remaining words are potential make/model
    result.keywords.push(word);
  }

  // Try to identify make and model from keywords
  // First keyword after year/color is usually make, second is model
  if (result.keywords.length >= 1) {
    result.make = result.keywords[0];
  }
  if (result.keywords.length >= 2) {
    result.model = result.keywords[1];
  }

  return result;
}

// =====================================================
// CUSTOMER QUERY PARSING
// =====================================================

export interface ParsedNameQuery {
  originalQuery: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  isEmail?: boolean;
  isPhone?: boolean;
  keywords: string[];
}

/**
 * Parse a customer search query to extract name components
 * Examples:
 *   "John Smith" -> { firstName: "john", lastName: "smith" }
 *   "Smith, John" -> { firstName: "john", lastName: "smith" }
 *   "Smith" -> { lastName: "smith" }
 *   "john.smith@email.com" -> { isEmail: true }
 *   "0412345678" -> { isPhone: true }
 */
export function parseNameQuery(query: string): ParsedNameQuery {
  const trimmed = query.trim();

  const result: ParsedNameQuery = {
    originalQuery: query,
    keywords: [],
  };

  // Check if it's an email
  if (/@/.test(trimmed)) {
    result.isEmail = true;
    result.keywords.push(trimmed.toLowerCase());
    return result;
  }

  // Check if it's a phone number (6+ digits with optional formatting)
  const digitsOnly = normalizePhoneNumber(trimmed);
  if (digitsOnly.length >= 6 && /^[0-9+\s()-]+$/.test(trimmed)) {
    result.isPhone = true;
    result.keywords.push(digitsOnly);
    return result;
  }

  // Handle "Last, First" format
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map(p => p.trim().toLowerCase());
    if (parts.length >= 2) {
      result.lastName = parts[0];
      result.firstName = parts[1];
      result.fullName = `${result.firstName} ${result.lastName}`;
      result.keywords = [result.firstName, result.lastName];
      return result;
    }
  }

  // Handle "First Last" or "Last" format
  const words = trimmed.toLowerCase().split(/\s+/).filter(w => w.length > 0);

  if (words.length === 1) {
    // Could be first name or last name - we'll search both
    result.keywords.push(words[0]);
  } else if (words.length === 2) {
    result.firstName = words[0];
    result.lastName = words[1];
    result.fullName = `${result.firstName} ${result.lastName}`;
    result.keywords = [result.firstName, result.lastName];
  } else if (words.length >= 3) {
    // Likely "First Middle Last" - take first and last
    result.firstName = words[0];
    result.lastName = words[words.length - 1];
    result.fullName = words.join(' ');
    result.keywords = words;
  }

  return result;
}

// =====================================================
// FUZZY MATCHING
// =====================================================

/**
 * Calculate Levenshtein distance between two strings
 * (measure of how many single-character edits are needed to change one word into another)
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,     // deletion
          dp[i][j - 1] + 1,     // insertion
          dp[i - 1][j - 1] + 1  // substitution
        );
      }
    }
  }

  return dp[m][n];
}

/**
 * Calculate similarity score between two strings (0-1, where 1 is identical)
 */
export function calculateSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;

  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();

  // Exact match
  if (s1 === s2) return 1;

  // One contains the other
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;

  // Calculate similarity based on Levenshtein distance
  const maxLength = Math.max(s1.length, s2.length);
  const distance = levenshteinDistance(s1, s2);
  return 1 - (distance / maxLength);
}

/**
 * Check if two strings are likely a typo of each other
 * (similarity > 0.7 threshold)
 */
export function isLikelyTypo(input: string, target: string): boolean {
  return calculateSimilarity(input, target) > 0.7;
}

/**
 * Get suggestions for potential typos from a list of candidates
 */
export function getTypoSuggestions(input: string, candidates: string[], limit = 5): string[] {
  if (!input || !candidates?.length) return [];

  const scoredCandidates = candidates
    .map(candidate => ({
      value: candidate,
      score: calculateSimilarity(input, candidate)
    }))
    .filter(item => item.score > 0.6) // Only suggest if reasonably similar
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  return scoredCandidates.map(item => item.value);
}

// =====================================================
// PHONE NUMBER FORMATTING
// =====================================================

/**
 * Format phone number for display
 * Examples:
 *   "0412345678" -> "0412 345 678"
 *   "61412345678" -> "+61 412 345 678"
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return '';

  const digits = normalizePhoneNumber(phone);

  // Australian mobile (04XX XXX XXX)
  if (digits.startsWith('04') && digits.length === 10) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  // International format starting with country code
  if (digits.startsWith('61') && digits.length === 11) {
    return `+61 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }

  // Default: group in chunks of 4
  return digits.match(/.{1,4}/g)?.join(' ') || digits;
}

// =====================================================
// RESULT RANKING
// =====================================================

export interface ScoredSearchResult<T> {
  item: T;
  score: number;
  matchType: 'exact' | 'starts_with' | 'contains' | 'fuzzy';
}

/**
 * Rank search results based on relevance to query
 */
export function rankSearchResults<T>(
  items: T[],
  query: string,
  getSearchableText: (item: T) => string
): ScoredSearchResult<T>[] {
  const normalizedQuery = normalizeQuery(query);

  const scored = items.map(item => {
    const searchableText = normalizeQuery(getSearchableText(item));
    let score = 0;
    let matchType: ScoredSearchResult<T>['matchType'] = 'fuzzy';

    // Exact match: highest score
    if (searchableText === normalizedQuery) {
      score = 100;
      matchType = 'exact';
    }
    // Starts with query: high score
    else if (searchableText.startsWith(normalizedQuery)) {
      score = 80;
      matchType = 'starts_with';
    }
    // Contains query: medium score
    else if (searchableText.includes(normalizedQuery)) {
      score = 60;
      matchType = 'contains';
    }
    // Fuzzy match: score based on similarity
    else {
      const similarity = calculateSimilarity(normalizedQuery, searchableText);
      score = similarity * 40; // Max 40 points for fuzzy match
      matchType = 'fuzzy';
    }

    return { item, score, matchType };
  });

  // Sort by score (descending)
  return scored.sort((a, b) => b.score - a.score);
}

// =====================================================
// COMMON MAKE/MODEL TYPOS
// =====================================================

/**
 * Map of common typos to correct values
 */
export const COMMON_VEHICLE_TYPOS: Record<string, string> = {
  // Makes
  'toyoya': 'toyota',
  'toyoyta': 'toyota',
  'toyoto': 'toyota',
  'hundai': 'hyundai',
  'hyundae': 'hyundai',
  'hundae': 'hyundai',
  'mazada': 'mazda',
  'mazduh': 'mazda',
  'nissan': 'nissan',
  'nisan': 'nissan',
  'nissaan': 'nissan',
  'volksw': 'volkswagen',
  'vw': 'volkswagen',
  'merceedes': 'mercedes',
  'mercedes-benz': 'mercedes',
  'mb': 'mercedes',

  // Models
  'camrey': 'camry',
  'camary': 'camry',
  'corrola': 'corolla',
  'corola': 'corolla',
  'hilx': 'hilux',
  'hiluck': 'hilux',
  'landcruiser': 'land cruiser',
  'landcruzer': 'land cruiser',
};

/**
 * Correct common typos in vehicle search query
 */
export function correctVehicleTypos(query: string): string {
  const normalized = normalizeQuery(query);
  const words = normalized.split(' ');

  const corrected = words.map(word => {
    return COMMON_VEHICLE_TYPOS[word] || word;
  });

  return corrected.join(' ');
}

// =====================================================
// SEARCH HISTORY
// =====================================================

export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  resultCount: number;
}

const SEARCH_HISTORY_KEY = 'search_history';
const MAX_HISTORY_ITEMS = 20;

/**
 * Save a search to local history
 */
export function saveSearchHistory(query: string, resultCount: number): void {
  if (!query.trim()) return;

  try {
    const history = getSearchHistory();

    // Remove duplicate if exists
    const filtered = history.filter(item => item.query !== query);

    // Add new item at the beginning
    filtered.unshift({
      query,
      timestamp: Date.now(),
      resultCount,
    });

    // Keep only recent items
    const trimmed = filtered.slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Failed to save search history:', error);
  }
}

/**
 * Get search history
 */
export function getSearchHistory(): SearchHistoryItem[] {
  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    if (!stored) return [];

    const history = JSON.parse(stored);

    // Filter out old items (older than 30 days)
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    return history.filter((item: SearchHistoryItem) => item.timestamp > thirtyDaysAgo);
  } catch (error) {
    console.error('Failed to load search history:', error);
    return [];
  }
}

/**
 * Clear search history
 */
export function clearSearchHistory(): void {
  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch (error) {
    console.error('Failed to clear search history:', error);
  }
}
